import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface NavigationOrderItem {
  id: string;
  nav_key: string;
  display_name: string;
  sort_order: number;
  is_visible: boolean;
}

export function useNavigationOrder() {
  return useQuery({
    queryKey: ['navigation-order'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('navigation_order')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return (data ?? []) as NavigationOrderItem[];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

export function useUpdateNavigationOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (items: NavigationOrderItem[]) => {
      // Update each item's sort_order
      for (let i = 0; i < items.length; i++) {
        const { error } = await supabase
          .from('navigation_order')
          .update({ sort_order: i })
          .eq('id', items[i].id);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['navigation-order'] });
    },
  });
}

export function useResetNavigationOrder() {
  const queryClient = useQueryClient();
  
  const defaultOrder = [
    'home', 'accounts', 'sales', 'expenditure', 'bills', 'wages',
    'cash-flow', 'cash-management', 'payables', 'receivables',
    'profit-distribution', 'loans', 'reports', 'settings'
  ];
  
  return useMutation({
    mutationFn: async () => {
      for (let i = 0; i < defaultOrder.length; i++) {
        const { error } = await supabase
          .from('navigation_order')
          .update({ sort_order: i })
          .eq('nav_key', defaultOrder[i]);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['navigation-order'] });
    },
  });
}
